import { useCallback, useEffect, useState } from 'react';
import { FlatList, View, Platform } from 'react-native';
import * as S from './style';
import THEME from '../../../config/theme';
import { StandardText } from '../../../config/theme/globalStyles';
import { useCustomTheme } from '../../../contexts/useCustomTheme';
import { userHasCatalogProductAccess } from '../../../utils/accessUtils';
import ProductItem from '../ProductItem';

export function ProductCategoryShelf({
  title,
  items,
  user,
  titleRightSlot,
  shelfId,
  canDrag = false,
  onReorder,
}) {
  const { customColor, customFont } = useCustomTheme();
  const [localItems, setLocalItems] = useState(null);
  const [dragState, setDragState] = useState({ from: null, over: null });

  useEffect(() => {
    setLocalItems(null);
  }, [items]);

  const data = localItems || items || [];
  const dndAccent = customColor?.primary || THEME.COLORS.PRIMARY_800;

  const handleDrop = (from, to) => {
    setDragState({ from: null, over: null });
    if (from == null || to == null || from === to || !onReorder) return;

    const reordered = [...data];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setLocalItems(reordered);
    onReorder(from, to, reordered);
  };

  const renderItem = useCallback(
    ({ item, index }) => {
      const productCard = (
        <ProductItem product={item} isLocked={!userHasCatalogProductAccess(item, user)} />
      );

      if (!canDrag || data.length <= 1) return productCard;

      const isDragging = dragState.from === index;
      const isDropTarget =
        dragState.from !== null && dragState.over === index && dragState.from !== index;
      const showLeftBar = isDropTarget && dragState.from > index;
      const showRightBar = isDropTarget && dragState.from < index;

      return (
        <div
          draggable
          onDragStart={(e) => {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData('text/plain', String(index));
            setDragState({ from: index, over: null });
          }}
          onDragOver={(e) => {
            e.preventDefault();
            e.dataTransfer.dropEffect = 'move';
            setDragState((state) => (state.over === index ? state : { ...state, over: index }));
          }}
          onDrop={(e) => {
            e.preventDefault();
            const from = Number(e.dataTransfer.getData('text/plain'));
            handleDrop(Number.isFinite(from) ? from : null, index);
          }}
          onDragEnd={() => setDragState({ from: null, over: null })}
          style={{
            display: 'flex',
            cursor: 'grab',
            opacity: isDragging ? 0.35 : 1,
            borderLeft: `3px solid ${showLeftBar ? dndAccent : 'transparent'}`,
            borderRight: `3px solid ${showRightBar ? dndAccent : 'transparent'}`,
            transition: 'opacity 120ms ease',
          }}
        >
          {productCard}
        </div>
      );
    },
    [canDrag, data.length, dragState, dndAccent, user]
  );

  return (
    <S.Container>
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginRight: '1rem',
        }}
      >
        <StandardText
          fontSize={
            customFont && customFont.fontMD ? customFont.fontMD : THEME.FONTSIZE.MEDIUM
          }
          style={{
            fontFamily:
              customFont && customFont.fontFamilyTitle
                ? customFont.fontFamilyTitle
                : THEME.FONTFAMILY.BOLD,
          }}
          color={
            customColor && customColor.text && customColor.text.app
              ? customColor.text.app
              : THEME.COLORS.TITLE_MAIN
          }
          textAlign="left"
          numberOfLines={1}
          margin="0rem 0rem 0rem 1rem"
        >
          {title}
        </StandardText>
        {titleRightSlot}
      </View>
      <FlatList
        data={data}
        keyExtractor={(item) => item.id}
        renderItem={renderItem}
        extraData={canDrag ? dragState : undefined}
        horizontal
        showsHorizontalScrollIndicator={false}
        style={{ paddingLeft: '1rem' }}
        contentContainerStyle={{ paddingVertical: 20, paddingRight: '1rem' }}
        scrollEnabled={Platform.OS === 'web' ? dragState.from === null : true}
      />
    </S.Container>
  );
}

export default ProductCategoryShelf;
